/**
 * Admin Grower Profile Editor.
 * Edits the public-facing grower profile and toggles publish state.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Save, Eye, EyeOff, AlertCircle, Check, Plus, Trash2, ImagePlus, UploadCloud } from "lucide-react";
import NZTerritoryMap from "@/components/NZTerritoryMap";

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne",
  "Taranaki", "Manawatu-Whanganui", "Hawke's Bay", "Wellington",
  "Tasman", "Nelson", "Marlborough", "West Coast", "Canterbury",
  "Otago", "Southland",
];

export default function GrowerProfileEditor({ growerId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [varietyDraft, setVarietyDraft] = useState("");
  const [galleryDraft, setGalleryDraft] = useState("");
  const [view, setView] = useState("edit"); // "edit" | "preview"
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/growers/${growerId}/profile`);
        setProfile(data.profile);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [growerId]);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const body = { ...profile };
      delete body.id; delete body.grower_id; delete body.user_id;
      delete body.published_at; delete body.created_at; delete body.updated_at;
      const { data } = await api.patch(`/admin/growers/${growerId}/profile`, body);
      setProfile(data.profile);
      setMsg("Profile saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!profile) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      const url = profile.show_on_public_site ? "unpublish" : "publish";
      await api.post(`/admin/growers/${growerId}/${url}`);
      const { data } = await api.get(`/admin/growers/${growerId}/profile`);
      setProfile(data.profile);
      setMsg(profile.show_on_public_site ? "Removed from public site." : "Published live on the public site.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addVariety = () => {
    if (!varietyDraft.trim()) return;
    setProfile((p) => ({ ...p, varieties: [...(p.varieties || []), varietyDraft.trim()] }));
    setVarietyDraft("");
  };
  const removeVariety = (idx) => setProfile((p) => ({ ...p, varieties: (p.varieties || []).filter((_, i) => i !== idx) }));

  const addGallery = () => {
    if (!galleryDraft.trim()) return;
    setProfile((p) => ({ ...p, gallery_urls: [...(p.gallery_urls || []), galleryDraft.trim()] }));
    setGalleryDraft("");
  };
  const removeGallery = (idx) => setProfile((p) => ({ ...p, gallery_urls: (p.gallery_urls || []).filter((_, i) => i !== idx) }));

  // Read a File → base64 data URL
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadPhoto = async (file, kind) => {
    if (!file || !file.type?.startsWith("image/")) {
      setErr("Please drop an image file.");
      return;
    }
    setUploading(true); setErr(""); setMsg("");
    try {
      const data_url = await fileToDataUrl(file);
      const { data } = await api.post("/admin/grower-photos/upload", { data_url });
      if (kind === "hero") {
        set("hero_photo_url", data.url);
      } else {
        setProfile((p) => ({ ...p, gallery_urls: [...(p.gallery_urls || []), data.url] }));
      }
      setMsg(`Image uploaded (${Math.round(data.size_bytes / 1024)} KB after compression).`);
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Overlay onClose={onClose}>
        <div className="font-serif italic text-[var(--bloom-ink-muted)] text-center py-20">Loading profile...</div>
      </Overlay>
    );
  }
  if (!profile) {
    return (
      <Overlay onClose={onClose}>
        <div className="font-serif italic text-[var(--bloom-accent)] text-center py-20">{err || "Could not load profile"}</div>
      </Overlay>
    );
  }

  const published = profile.show_on_public_site;

  return (
    <Overlay onClose={onClose}>
      <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
        <button onClick={onClose} className="bloom-btn bloom-btn-outline !text-xs" data-testid="profile-editor-close">
          <X size={14} className="mr-1.5 inline" /> Close
        </button>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setView("edit")}
            className={`bloom-btn !text-xs ${view === "edit" ? "bloom-btn-primary" : "bloom-btn-outline"}`}
            data-testid="profile-view-edit"
          >Edit</button>
          <button
            onClick={() => setView("preview")}
            className={`bloom-btn !text-xs ${view === "preview" ? "bloom-btn-primary" : "bloom-btn-outline"}`}
            data-testid="profile-view-preview"
          >Preview</button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePublish}
            disabled={saving}
            className={`bloom-btn !text-xs ${published ? "bloom-btn-outline" : "bloom-btn-primary"}`}
            data-testid="profile-toggle-publish"
          >
            {published ? <><EyeOff size={12} className="mr-1.5 inline" /> Hide from site</> : <><Eye size={12} className="mr-1.5 inline" /> Publish to site</>}
          </button>
          <button onClick={save} disabled={saving} className="bloom-btn bloom-btn-primary !text-xs" data-testid="profile-save">
            <Save size={12} className="mr-1.5 inline" /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      {view === "preview" ? (
        <PreviewPanel profile={profile} />
      ) : (
      <div className="p-6 md:p-10 max-w-3xl">
        <div className="bloom-divider mb-4 text-left">✦ Grower Profile Editor ✦</div>
        <h2 className="font-serif text-4xl mb-1">{profile.display_name || profile.farm_name || "Untitled grower"}</h2>
        <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-6">
          {published ? <span className="inline-flex items-center gap-1.5 text-[var(--bloom-brand)]"><Check size={12} /> Live on the public site</span> : "Draft, hidden from the public site"}
        </div>

        {err && <div className="mb-4 p-3 border border-[var(--bloom-accent)] text-[var(--bloom-accent)] font-serif italic flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}
        {msg && <div className="mb-4 p-3 border border-[var(--bloom-brand)] text-[var(--bloom-brand)] font-serif italic flex items-center gap-2"><Check size={14} /> {msg}</div>}

        <Section title="Identity">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Display name"><input className="bloom-input" value={profile.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="profile-display-name" /></Field>
            <Field label="Farm name"><input className="bloom-input" value={profile.farm_name || ""} onChange={(e) => set("farm_name", e.target.value)} /></Field>
            <Field label="Tagline (one line)"><input className="bloom-input" value={profile.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Heritage dahlias from the Hawkes Bay sun." /></Field>
            <Field label="Instagram handle"><input className="bloom-input" value={profile.instagram_handle || ""} onChange={(e) => set("instagram_handle", e.target.value)} placeholder="@heritagebloom" /></Field>
            <div className="md:col-span-2">
              <Field label="Website"><input className="bloom-input" value={profile.website_url || ""} onChange={(e) => set("website_url", e.target.value)} placeholder="https://" /></Field>
            </div>
          </div>
        </Section>

        <Section title="Territory">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Region">
              <select className="bloom-input" value={profile.region || ""} onChange={(e) => set("region", e.target.value)} data-testid="profile-region">
                <option value="">Choose region</option>
                {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Town / city"><input className="bloom-input" value={profile.town_city || ""} onChange={(e) => set("town_city", e.target.value)} placeholder="Havelock North" /></Field>
          </div>
          <div className="mt-4">
            <NZTerritoryMap pins={profile.region ? [{ region: profile.region, count: 1 }] : []} activeRegion={profile.region} size="compact" showLegend={false} />
          </div>
        </Section>

        <Section title="Story">
          <Field label="Vision / about (shown publicly)">
            <textarea rows={5} className="bloom-input" style={{ resize: "vertical" }} value={profile.story || ""} onChange={(e) => set("story", e.target.value)} />
          </Field>
        </Section>

        <Section title="Varieties grown">
          <div className="flex gap-2 mb-3">
            <input className="bloom-input" placeholder="e.g. Cafe au Lait" value={varietyDraft} onChange={(e) => setVarietyDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVariety())} />
            <button onClick={addVariety} className="bloom-btn bloom-btn-outline !text-xs"><Plus size={12} className="mr-1 inline" /> Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(profile.varieties || []).map((v, i) => (
              <span key={`${v}-${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bloom-surface)] text-[var(--bloom-ink)] text-xs font-serif italic" style={{ borderRadius: 999 }}>
                {v}
                <button onClick={() => removeVariety(i)} className="opacity-60 hover:opacity-100"><X size={10} /></button>
              </span>
            ))}
          </div>
        </Section>

        <Section title="Hero photo">
          <DropZone
            label="Drag a photo here, or click to choose"
            onFile={(f) => uploadPhoto(f, "hero")}
            busy={uploading}
            testid="hero-dropzone"
          />
          <div className="mt-3">
            <Field label="...or paste a URL"><input className="bloom-input" value={profile.hero_photo_url || ""} onChange={(e) => set("hero_photo_url", e.target.value)} placeholder="https://..." /></Field>
          </div>
          {profile.hero_photo_url && (
            <div className="mt-3 aspect-[16/9] overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)] relative group" data-testid="hero-preview">
              <img src={profile.hero_photo_url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => set("hero_photo_url", "")} className="absolute top-2 right-2 p-1.5 bg-[var(--bloom-ink)]/80 text-white opacity-0 group-hover:opacity-100 transition" title="Remove hero photo">
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </Section>

        <Section title="Gallery">
          <DropZone
            label="Drag photos here to add to the gallery"
            onFile={(f) => uploadPhoto(f, "gallery")}
            busy={uploading}
            testid="gallery-dropzone"
          />
          <div className="flex gap-2 mt-3 mb-3">
            <input className="bloom-input" placeholder="...or paste a URL" value={galleryDraft} onChange={(e) => setGalleryDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGallery())} />
            <button onClick={addGallery} className="bloom-btn bloom-btn-outline !text-xs"><ImagePlus size={12} className="mr-1 inline" /> Add</button>
          </div>
          {(profile.gallery_urls || []).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {profile.gallery_urls.map((u, i) => (
                <div key={`${u}-${i}`} className="relative aspect-square overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeGallery(i)} className="absolute top-1 right-1 p-1 bg-[var(--bloom-ink)]/80 text-white">
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Visibility toggles">
          <Toggle label="Show on public Meet the Growers page" checked={profile.show_on_public_site} onChange={(v) => set("show_on_public_site", v)} />
          <Toggle label="Show on public Territory map" checked={profile.show_in_territory_map} onChange={(v) => set("show_in_territory_map", v)} />
          <Toggle label="Available for subscriber matching" checked={profile.show_in_subscriber_match} onChange={(v) => set("show_in_subscriber_match", v)} />
        </Section>
      </div>
      )}
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm" onClick={onClose} data-testid="grower-profile-editor">
      <div className="absolute right-0 top-0 bottom-0 w-full md:w-[800px] bg-[var(--bloom-bg)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h3 className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">{title}</h3>
      <div className="bloom-card p-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-2.5 cursor-pointer">
      <span className="font-serif text-base">{label}</span>
      <input
        type="checkbox"
        className="w-5 h-5"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

function DropZone({ label, onFile, busy, testid }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };
  const handleChoose = (e) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    e.target.value = "";
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed transition cursor-pointer p-8 text-center ${
        drag ? "border-[var(--bloom-brand)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] hover:border-[var(--bloom-brand)] bg-white/30"
      }`}
      style={{ borderRadius: 6 }}
      data-testid={testid}
    >
      <UploadCloud size={28} strokeWidth={1.4} className={`mx-auto mb-2 ${drag ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-ink-muted)]"}`} />
      <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)]">
        {busy ? "Uploading and compressing..." : label}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleChoose} className="hidden" />
    </div>
  );
}

function PreviewPanel({ profile }) {
  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="profile-preview-panel">
      <div className="bloom-divider mb-4 text-left">✦ Public preview ✦</div>
      <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-6">
        This is how your grower profile will appear on the public site once you press Publish.
      </p>

      <div className="bloom-card overflow-hidden">
        {profile.hero_photo_url ? (
          <div className="aspect-[16/9] bg-[var(--bloom-surface)] overflow-hidden">
            <img src={profile.hero_photo_url} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[16/9] bg-[var(--bloom-surface)] flex items-center justify-center font-serif italic text-[var(--bloom-ink-muted)]">
            (Add a hero photo to fill this space.)
          </div>
        )}
        <div className="p-6 md:p-8">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">
            Founding Grower
          </div>
          <h2 className="font-serif text-4xl">{profile.farm_name || "Unnamed farm"}</h2>
          <div className="font-serif italic text-[var(--bloom-ink-soft)] mt-1">
            {profile.display_name || "Anonymous"}{profile.town_city ? `, ${profile.town_city}` : ""}{profile.region ? `, ${profile.region}` : ""}
          </div>
          {profile.tagline && (
            <p className="mt-4 font-serif italic text-lg text-[var(--bloom-ink-soft)]">"{profile.tagline}"</p>
          )}
          {profile.story && (
            <p className="mt-6 font-serif leading-relaxed text-[var(--bloom-ink-soft)]">{profile.story}</p>
          )}
          {(profile.varieties || []).length > 0 && (
            <div className="mt-6">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-2">Varieties grown</div>
              <div className="flex flex-wrap gap-2">
                {(profile.varieties || []).map((v, i) => (
                  <span key={`${v}-${i}`} className="px-3 py-1 bg-[var(--bloom-surface)] text-xs font-serif italic" style={{ borderRadius: 999 }}>{v}</span>
                ))}
              </div>
            </div>
          )}
          {(profile.gallery_urls || []).length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
              {profile.gallery_urls.map((u, i) => (
                <div key={`${u}-${i}`} className="aspect-square overflow-hidden bg-[var(--bloom-surface)]">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          {(profile.instagram_handle || profile.website_url) && (
            <div className="mt-6 pt-6 border-t border-[var(--bloom-border)] flex flex-wrap gap-4 text-sm font-serif">
              {profile.instagram_handle && <span>{profile.instagram_handle}</span>}
              {profile.website_url && <a href={profile.website_url} target="_blank" rel="noreferrer" className="underline text-[var(--bloom-brand)]">{profile.website_url}</a>}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bloom-card p-5">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">Region on the map</div>
        <NZTerritoryMap pins={profile.region ? [{ region: profile.region, count: 1 }] : []} activeRegion={profile.region} size="compact" showLegend={false} />
      </div>
    </div>
  );
}
